import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { EquipmentForm, StockPicker, emptyEquipmentForm, buildEquipmentPayload } from "../inventory/EquipmentForm";
import { ItemDocuments } from "../inventory/ItemDocuments";
import { HandoverSection, emptyHandover, CONDITION_LABELS, ConditionBadge } from "../inventory/HandoverSection";
import { RebillBadge, rebillStatus } from "../inventory/RebillFields";
import { getCategoryIcon } from "../inventory/categoryIcons";
import { LeftCompanyBanner, LeftCompanyBadge } from "../shared/LeftCompany";
import { hasLeftCompany } from "../../lib/utils";
import { HOLDER_END_LABELS } from "../inventory/ItemHoldersDialog";
import { ReturnDialog } from "../inventory/ReturnDialog";
import { QrLabelDialog } from "../inventory/QrLabelDialog";
import { isValidUrl, linkProvider } from "../shared/LinkDocDialog";
import { EmployeeSignatureBadge, ItemSignatureBadge, SIG_CARD_CLASS } from "../inventory/SignatureBadges";
import {
    ArrowLeft, Plus, Clock, Pencil, Trash2, RefreshCw, ArrowLeftRight, Undo2, Archive, Mail, Building2, QrCode, FileSignature, Upload, History
} from "lucide-react";

const HISTORY_ICONS = {
    created: "📦", assigned: "👤", returned_to_stock: "↩️", transferred: "🔄",
    maintenance_start: "🔧", retired: "🗑️", lost: "❌", broken: "💥",
    modified: "✏️", replaced: "🔁", tag_changed: "🏷️", signed_doc_uploaded: "✍️",
};

const OUTCOME_LABELS = { in_stock: "retour en stock", retired: "retiré du service", broken: "défectueux" };

export default function InventoryEmployeeSheet() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { departments, hasPermission, currentUser } = useApp();
    const [sheet, setSheet] = useState(null);
    const [categories, setCategories] = useState([]);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);

    const [addOpen, setAddOpen] = useState(false);
    const [replaceItem, setReplaceItem] = useState(null);
    const [returnItem, setReturnItem] = useState(null);
    const [reassignItem, setReassignItem] = useState(null);
    const [reassignTarget, setReassignTarget] = useState(null);
    const [qrItem, setQrItem] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [empForm, setEmpForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [scanType, setScanType] = useState("remise_signee");
    const [scanDate, setScanDate] = useState(new Date().toISOString().slice(0, 10));
    const [scanFile, setScanFile] = useState(null);
    const [scanMode, setScanMode] = useState("file");
    const [scanUrl, setScanUrl] = useState("");
    const [scanUploading, setScanUploading] = useState(false);

    const isAdmin = hasPermission("admin");
    const canManage = hasPermission("inventory:manage");

    const load = useCallback(async () => {
        try {
            const [s, cats, emps] = await Promise.all([
                api.getEmployeeSheet(id),
                api.getInventoryCategories(),
                api.getInventoryEmployees(),
            ]);
            setSheet(s);
            setCategories(cats);
            setPeople(emps.employees);
        } catch {
            toast.error("Employé introuvable");
            navigate("/admin/inventory");
        } finally { setLoading(false); }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const deptMap = React.useMemo(() => {
        const m = {};
        departments.forEach((d) => { m[d.id] = d.name; });
        return m;
    }, [departments]);

    const peopleList = React.useMemo(
        () => people.map((p) => ({ id: p.id, first_name: p.first_name, last_name: p.last_name, email: p.email || "", department_id: p.department_id })),
        [people],
    );
    const reassignList = React.useMemo(() => peopleList.filter((p) => p.id !== id), [peopleList, id]);

    const handleScanUpload = async () => {
        if (scanMode === "file" && !scanFile) { toast.error("Sélectionnez le scan signé"); return; }
        if (scanMode === "link" && !isValidUrl(scanUrl)) { toast.error("URL invalide (http:// ou https://)"); return; }
        setScanUploading(true);
        try {
            const res = scanMode === "link"
                ? await api.linkEmployeeSignedDoc(id, { url: scanUrl.trim(), doc_type: scanType, signed_date: scanDate })
                : await api.uploadEmployeeSignedDoc(id, scanFile, scanType, scanDate);
            toast.success(`${scanMode === "link" ? "Lien du scan signé" : "Scan signé"} enregistré sur ${res.count} équipement(s) — date : ${new Date(scanDate).toLocaleDateString("fr-CH")}`);
            setScanOpen(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
        finally { setScanUploading(false); }
    };

    const handleRetire = async (item) => {
        if (!window.confirm(`Retirer "${item.asset_tag || item.name}" du service (fin de vie) ?`)) return;
        try { await api.retireInventoryItem(item.id, {}); toast.success("Retiré du service"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleReassign = async () => {
        if (!reassignTarget) return;
        try {
            await api.transferInventoryItem(reassignItem.id, { to_user_id: reassignTarget });
            toast.success("Matériel réattribué");
            setReassignItem(null);
            setReassignTarget(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const openEditEmp = () => {
        const e = sheet.employee;
        setEmpForm({ first_name: e.first_name || "", last_name: e.last_name || "", email: e.email || "", employee_id: e.employee_id || "", department_id: e.department_id || "", position: e.position || "", notes: e.notes || "" });
        setEditOpen(true);
    };

    const handleSaveEmp = async () => {
        setSaving(true);
        try {
            await api.updateInventoryEmployee(id, empForm);
            toast.success("Fiche mise à jour");
            setEditOpen(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const handleDeleteEmp = async () => {
        if (!window.confirm("Supprimer définitivement cette fiche employé ?")) return;
        try {
            await api.deleteInventoryEmployee(id);
            toast.success("Fiche supprimée");
            navigate("/admin/inventory");
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    if (loading) return <div className="page-container"><div className="animate-pulse text-gray-400">Chargement de la fiche...</div></div>;
    if (!sheet) return null;

    const emp = sheet.employee;
    const names = sheet.person_names || {};
    const isLocal = emp.source === "local";
    const signedItems = sheet.items.filter((i) => i.remise_signed === true).length;
    const unsignedItems = sheet.items.filter((i) => i.remise_signed === false).length;
    const sigSummary = { signed_items: signedItems, unsigned_items: unsignedItems, signed_status: signedItems + unsignedItems === 0 ? null : unsignedItems === 0 ? "all" : signedItems === 0 ? "none" : "partial" };

    return (
        <div className="page-container" data-testid="employee-sheet-page">
            <button onClick={() => navigate("/admin/inventory")} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-inventory">
                <ArrowLeft className="w-4 h-4" />Inventaire
            </button>

            <LeftCompanyBanner user={emp} context="collaborateur" className="mb-4" />

            {/* Employee header */}
            <div className={`card mb-4 ${SIG_CARD_CLASS[sigSummary.signed_status] || ""} ${hasLeftCompany(emp) ? "border-rose-200 bg-rose-50/30" : ""}`} data-testid="employee-header-card" data-signed-status={sigSummary.signed_status || "none-items"}>
                <div className="card-body">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-base font-bold flex-shrink-0">
                            {(emp.first_name?.[0] || "") + (emp.last_name?.[0] || "")}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-lg font-bold text-gray-900" data-testid="employee-name">{emp.first_name} {emp.last_name}</h1>
                                {emp.employee_id && <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono" title="N° employé (Microsoft 365)" data-testid="employee-sheet-id">N° employé {emp.employee_id}</span>}
                                <LeftCompanyBadge user={emp} testId="employee-sheet-left-badge" />
                                {isLocal
                                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Employé local (sans compte)</span>
                                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Compte MS365</span>}
                                <EmployeeSignatureBadge employee={sigSummary} size="sm" testId="employee-sheet-sig" />
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                                {emp.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{emp.email}</span>}
                                {emp.department_id && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{deptMap[emp.department_id] || "—"}</span>}
                                {(emp.job_title || emp.position) && <span>{emp.job_title || emp.position}</span>}
                            </div>
                            {isLocal && emp.notes && <p className="text-xs text-gray-400 mt-1">{emp.notes}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-xl font-bold text-gray-800" data-testid="employee-items-count">{sheet.items.length}</p>
                            <p className="text-[10px] text-gray-500">équipement(s)</p>
                            {sheet.total_value > 0 && <p className="text-xs font-medium text-gray-600 mt-1">{sheet.total_value.toLocaleString("fr-CH")} CHF</p>}
                        </div>
                        {isLocal && (
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                                <Button size="sm" variant="outline" onClick={openEditEmp} data-testid="edit-employee-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
                                {isAdmin && <Button size="sm" variant="outline" className="text-red-600" onClick={handleDeleteEmp} data-testid="delete-employee-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer</Button>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Equipment */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h2 className="text-sm font-semibold text-gray-800">Matériel attribué</h2>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {sheet.items.length > 0 && (
                                <>
                                    <Button size="sm" variant="outline" onClick={() => window.open(api.employeeHandoverDocUrl(id, "remise"), "_blank")} data-testid="employee-doc-remise-btn"><FileSignature className="w-3.5 h-3.5 mr-1" />Doc. remise</Button>
                                    <Button size="sm" variant="outline" onClick={() => window.open(api.employeeHandoverDocUrl(id, "restitution"), "_blank")} data-testid="employee-doc-restitution-btn"><FileSignature className="w-3.5 h-3.5 mr-1" />Doc. restitution</Button>
                                    <Button size="sm" variant="outline" onClick={() => { setScanFile(null); setScanUrl(""); setScanOpen(true); }} data-testid="employee-scan-btn"><Upload className="w-3.5 h-3.5 mr-1" />Scan signé</Button>
                                </>
                            )}
                            {canManage && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-equipment-btn"><Plus className="w-4 h-4 mr-1" />Ajouter du matériel</Button>}
                        </div>
                    </div>

                    {sheet.items.length === 0 && (
                        <div className="card"><div className="card-body py-8 text-center text-sm text-gray-400">Aucun matériel attribué</div></div>
                    )}

                    {sheet.items.map((item) => {
                        const Icon = getCategoryIcon(categories, item.category);
                        const catLabel = categories.find((c) => c.key === item.category)?.label || item.category;
                        return (
                            <div key={item.id} className={`card ${item.remise_signed === true ? "sig-all" : item.remise_signed === false ? "sig-none" : ""}`} data-testid={`equipment-card-${item.id}`} data-remise-signed={String(item.remise_signed)}>
                                <div className="card-body">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4.5 h-4.5 text-gray-600" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button onClick={() => navigate(`/admin/inventory/${item.id}`)} className="text-sm font-semibold text-gray-900 hover:text-teal-600 hover:underline font-mono" data-testid={`equipment-name-${item.id}`}>{item.asset_tag || item.name}</button>
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{catLabel}</span>
                                                <ItemSignatureBadge item={item} testId={`equipment-sig-${item.id}`} />
                                                {(item.condition || item.handover_condition) && <ConditionBadge condition={item.condition || item.handover_condition} testId={`emp-condition-${item.id}`} />}
                                                {getWarrantyBadge(item)}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {item.name && item.asset_tag && <span className="text-gray-700 font-medium mr-2">{item.name}</span>}
                                                {[item.brand, item.model].filter(Boolean).join(" ")}
                                                {item.serial_number && <span className="ml-2 font-mono text-gray-400">SN: {item.serial_number}</span>}
                                            </p>
                                            <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
                                                {item.purchase_price != null && item.purchase_price !== "" && <span>{item.purchase_price} {item.purchase_currency || "CHF"}</span>}
                                                {item.purchase_date && <span>Acheté le {new Date(item.purchase_date).toLocaleDateString("fr-CH")}</span>}
                                                {item.invoice_number && <span>Facture {item.invoice_number}</span>}
                                                {item.warranty_end && <span>Garantie jusqu'au {new Date(item.warranty_end).toLocaleDateString("fr-CH")}</span>}
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5 text-[11px] flex-wrap">
                                                {item.handover_date_undefined ? (
                                                    <span className="text-gray-500" data-testid={`handover-info-${item.id}`}>
                                                        Remis (date non définie)
                                                        {item.handed_over_by && names[item.handed_over_by] ? ` par ${names[item.handed_over_by]}` : ""}
                                                    </span>
                                                ) : item.handover_date ? (
                                                    <span className="text-gray-500" data-testid={`handover-info-${item.id}`}>
                                                        Remis le {new Date(item.handover_date).toLocaleDateString("fr-CH")}
                                                        {item.handed_over_by && names[item.handed_over_by] ? ` par ${names[item.handed_over_by]}` : ""}
                                                    </span>
                                                ) : item.assigned_at ? (
                                                    <span className="text-gray-400">Attribué le {new Date(item.assigned_at).toLocaleDateString("fr-CH")}</span>
                                                ) : null}
                                                {item.handover_condition && <span className="text-teal-600">État à la remise : {CONDITION_LABELS[item.handover_condition] || item.handover_condition}</span>}
                                            </div>
                                            {rebillStatus(item) !== "none" && (
                                                <div className="mt-1"><RebillBadge item={item} testId={`sheet-rebill-${item.id}`} /></div>
                                            )}
                                            {(Object.keys(item.specs || {}).length > 0 || (item.extra_fields || []).length > 0) && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {Object.entries(item.specs || {}).filter(([, v]) => v).map(([k, v]) => (
                                                        <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-100">{k}: {v}</span>
                                                    ))}
                                                    {(item.extra_fields || []).filter((f) => f.value).map((f, i) => (
                                                        <span key={`xf-${i}`} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">{f.label}: {f.value}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-2 border-t border-gray-50">
                                        <ItemDocuments itemId={item.id} attachments={item.attachments || []} onChanged={load} compact itemStatus={item.status} />
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-gray-50 flex-wrap">
                                        <Button size="sm" variant="outline" onClick={() => setReplaceItem(item)} data-testid={`replace-btn-${item.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Remplacer</Button>
                                        <Button size="sm" variant="outline" onClick={() => { setReassignItem(item); setReassignTarget(null); }} data-testid={`reassign-btn-${item.id}`}><ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Réattribuer</Button>
                                        <Button size="sm" variant="outline" onClick={() => setReturnItem(item)} data-testid={`return-btn-${item.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Restituer</Button>
                                        <Button size="sm" variant="outline" className="text-orange-600" onClick={() => handleRetire(item)} data-testid={`retire-btn-${item.id}`}><Archive className="w-3.5 h-3.5 mr-1" />Retirer</Button>
                                        <Button size="sm" variant="outline" onClick={() => setQrItem(item)} data-testid={`qr-btn-${item.id}`}><QrCode className="w-3.5 h-3.5 mr-1" />Étiquette</Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Shared equipment (department) where this person is responsible / intended user */}
                    {(sheet.shared_items || []).length > 0 && (
                        <div className="card border-teal-200" data-testid="shared-equipment-card">
                            <div className="card-header flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-teal-600" />
                                <h3 className="text-sm font-semibold text-gray-800">Matériel partagé ({sheet.shared_items.length})</h3>
                                <span className="text-[10px] text-gray-400">attribué à un département — responsable ou utilisateur prévu</span>
                            </div>
                            <div className="card-body py-1">
                                {sheet.shared_items.map((it) => {
                                    const SIcon = getCategoryIcon(categories, it.category);
                                    return (
                                        <div key={it.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded" onClick={() => navigate(`/admin/inventory/${it.id}`)} data-testid={`shared-item-${it.id}`}>
                                            <span className="w-8 h-8 rounded-md bg-teal-50 inline-flex items-center justify-center text-teal-700 flex-shrink-0"><SIcon className="w-4 h-4" /></span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900 truncate">{it.name || [it.brand, it.model].filter(Boolean).join(" ") || it.asset_tag}</p>
                                                <p className="text-[11px] text-gray-500 truncate font-mono">{it.asset_tag}{it.serial_number ? ` · SN ${it.serial_number}` : ""}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-xs text-teal-700 font-medium inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{it.assigned_department_name || "Département"}</p>
                                                <p className="text-[10px] text-gray-500">{it.shared_role === "responsible" ? "Responsable" : "Utilisateur prévu"}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Past equipment */}
                    {(sheet.past_items || []).length > 0 && (
                        <div className="card" data-testid="past-equipment-card">
                            <div className="card-header flex items-center gap-2">
                                <History className="w-4 h-4 text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-800">Matériel passé ({sheet.past_items.length})</h3>
                            </div>
                            <div className="card-body py-1">
                                {sheet.past_items.map((p) => {
                                    const PIcon = getCategoryIcon(categories, p.item?.category);
                                    return (
                                        <div key={p.item.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={`past-item-${p.item.id}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <PIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <button onClick={() => navigate(`/admin/inventory/${p.item.id}`)} className="text-xs font-semibold text-gray-800 hover:text-teal-600 hover:underline font-mono truncate block">{p.item.asset_tag || p.item.name}</button>
                                                    <p className="text-[10px] text-gray-400 truncate">{p.item.name && p.item.asset_tag ? p.item.name : ""}{p.item.serial_number ? ` SN: ${p.item.serial_number}` : ""}</p>
                                                </div>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-[11px] text-gray-500">
                                                    {p.start ? `Du ${new Date(p.start).toLocaleDateString("fr-CH")}` : ""}
                                                    {p.end ? ` au ${new Date(p.end).toLocaleDateString("fr-CH")}` : ""}
                                                </p>
                                                {p.end_action && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{HOLDER_END_LABELS[p.end_action] || p.end_action}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* History */}
                <div>
                    <div className="card">
                        <div className="card-header flex items-center gap-2"><Clock className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-800">Historique</h3></div>
                        <div className="card-body p-0">
                            <div className="px-4 py-2 space-y-3 max-h-[600px] overflow-y-auto" data-testid="employee-history">
                                {sheet.history.map((h) => (
                                    <div key={h.id} className="flex gap-3 py-1 border-b border-gray-50 last:border-0" data-testid={`emp-hist-${h.id}`}>
                                        <span className="text-sm flex-shrink-0">{HISTORY_ICONS[h.action] || "•"}</span>
                                        <div className="flex-1 min-w-0">
                                            {h.item && (
                                                <button onClick={() => navigate(`/admin/inventory/${h.item.id}`)} className="text-xs font-semibold text-gray-800 hover:text-teal-600 hover:underline font-mono">{h.item.asset_tag || h.item.name}</button>
                                            )}
                                            <p className="text-xs text-gray-600">{formatSheetAction(h, names, id)}</p>
                                            {h.details?.condition && <p className="text-[10px] text-gray-500">État : {CONDITION_LABELS[h.details.condition] || h.details.condition}</p>}
                                            {h.details?.return_condition && <p className="text-[10px] text-gray-500">État à la restitution : {CONDITION_LABELS[h.details.return_condition] || h.details.return_condition}</p>}
                                            {h.details?.condition_notes && <p className="text-[10px] text-gray-400 italic">{h.details.condition_notes}</p>}
                                            {h.details?.notes && <p className="text-[10px] text-gray-400 mt-0.5">{h.details.notes}</p>}
                                            {(h.photos || []).length > 0 && (
                                                <div className="flex gap-1 mt-1 flex-wrap">
                                                    {h.photos.map((p) => (
                                                        <a key={p.id} href={api.inventoryAttachmentUrl(p.id)} target="_blank" rel="noreferrer" title={p.display_name}>
                                                            <img src={api.inventoryAttachmentUrl(p.id)} alt={p.display_name} className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80" />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="text-[10px] text-gray-400">{new Date(h.timestamp).toLocaleString("fr-CH")}</p>
                                        </div>
                                    </div>
                                ))}
                                {sheet.history.length === 0 && <p className="text-xs text-gray-400 py-4 text-center">Aucun historique</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add equipment dialog */}
            <AddEquipmentDialog open={addOpen} onClose={() => setAddOpen(false)} categories={categories} employeeId={id} onDone={load}
                people={peopleList} departments={departments} currentUserId={currentUser?.id} />

            {/* Replace dialog */}
            <ReplaceDialog item={replaceItem} onClose={() => setReplaceItem(null)} categories={categories} onDone={load}
                people={peopleList} departments={departments} currentUserId={currentUser?.id} />

            {/* Return dialog */}
            <ReturnDialog item={returnItem} onClose={() => setReturnItem(null)} onDone={load} people={peopleList} departments={departments} />

            {/* QR Label dialog */}
            <QrLabelDialog item={qrItem} open={!!qrItem} onClose={() => setQrItem(null)} onLabeled={load} />

            <Dialog open={scanOpen} onOpenChange={(o) => { if (!o) setScanOpen(false); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Déposer le scan signé</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-1" data-testid="scan-signed-dialog">
                        <div>
                            <label className="form-label text-xs">Type de document</label>
                            <select value={scanType} onChange={(e) => setScanType(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="scan-type-select">
                                <option value="remise_signee">Remise signée</option>
                                <option value="restitution_signee">Restitution signée</option>
                            </select>
                        </div>
                        <div>
                            <label className="form-label text-xs">Date de signature</label>
                            <input type="date" value={scanDate} onChange={(e) => setScanDate(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="scan-date-input" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="form-label text-xs mb-0">{scanMode === "link" ? "Lien vers le scan signé" : "Scan signé (PDF ou photo)"}</label>
                                <div className="inline-flex rounded-md border overflow-hidden h-7" data-testid="scan-mode-toggle">
                                    <button type="button" onClick={() => setScanMode("file")} className={`px-2 text-[11px] ${scanMode === "file" ? "bg-teal-600 text-white" : "bg-white text-gray-600"}`} data-testid="scan-mode-file">Fichier</button>
                                    <button type="button" onClick={() => setScanMode("link")} className={`px-2 text-[11px] border-l ${scanMode === "link" ? "bg-sky-600 text-white" : "bg-white text-gray-600"}`} data-testid="scan-mode-link">Lien</button>
                                </div>
                            </div>
                            {scanMode === "link" ? (
                                <>
                                    <input type="url" value={scanUrl} onChange={(e) => setScanUrl(e.target.value)} placeholder="https://… (OneDrive, SharePoint…)"
                                        className="w-full px-3 py-2 text-sm border rounded-md font-mono text-xs" data-testid="scan-url-input" />
                                    {isValidUrl(scanUrl) && <p className="text-[10px] text-sky-600 mt-1">{linkProvider(scanUrl)} — seul le lien sera enregistré</p>}
                                </>
                            ) : (
                                <input type="file" accept=".pdf,.png,.jpg,.jpeg"
                                    onChange={(e) => setScanFile(e.target.files?.[0] || null)}
                                    className="block w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                                    data-testid="scan-file-input" />
                            )}
                        </div>
                        <p className="text-[10px] text-gray-400">
                            Le document sera attaché à chacun des {sheet.items.length} équipement(s) attribué(s), la date sera enregistrée et tracée dans l'historique.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScanOpen(false)}>Annuler</Button>
                        <Button onClick={handleScanUpload} disabled={scanUploading || (scanMode === "file" ? !scanFile : !isValidUrl(scanUrl))} data-testid="scan-upload-btn">{scanUploading ? "Envoi..." : "Enregistrer"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reassign dialog */}
            <Dialog open={!!reassignItem} onOpenChange={(o) => { if (!o) setReassignItem(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Réattribuer "{reassignItem?.asset_tag || reassignItem?.name}"</DialogTitle></DialogHeader>
                    <div className="py-2">
                        <label className="form-label text-xs">Nouveau détenteur</label>
                        <UserSearchSelect users={reassignList} departments={departments} value={reassignTarget} onChange={setReassignTarget} placeholder="Rechercher un employé..." />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReassignItem(null)}>Annuler</Button>
                        <Button onClick={handleReassign} disabled={!reassignTarget} data-testid="confirm-reassign-btn">Réattribuer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit local employee dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Modifier la fiche employé</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Prénom *</label><Input value={empForm.first_name || ""} onChange={(e) => setEmpForm({ ...empForm, first_name: e.target.value })} data-testid="edit-emp-first-name" /></div>
                            <div><label className="form-label text-xs">Nom *</label><Input value={empForm.last_name || ""} onChange={(e) => setEmpForm({ ...empForm, last_name: e.target.value })} data-testid="edit-emp-last-name" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Email</label><Input value={empForm.email || ""} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} data-testid="edit-emp-email" /></div>
                            <div><label className="form-label text-xs">N° d'employé</label><Input value={empForm.employee_id || ""} onChange={(e) => setEmpForm({ ...empForm, employee_id: e.target.value })} className="font-mono" data-testid="edit-emp-employee-id" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Département</label>
                                <select value={empForm.department_id || ""} onChange={(e) => setEmpForm({ ...empForm, department_id: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="edit-emp-department">
                                    <option value="">—</option>
                                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div><label className="form-label text-xs">Fonction</label><Input value={empForm.position || ""} onChange={(e) => setEmpForm({ ...empForm, position: e.target.value })} data-testid="edit-emp-position" /></div>
                        </div>
                        <div><label className="form-label text-xs">Notes</label><Input value={empForm.notes || ""} onChange={(e) => setEmpForm({ ...empForm, notes: e.target.value })} data-testid="edit-emp-notes" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
                        <Button onClick={handleSaveEmp} disabled={saving} data-testid="confirm-edit-employee-btn">{saving ? "..." : "Sauvegarder"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AddEquipmentDialog({ open, onClose, categories, employeeId, onDone, people, departments, currentUserId }) {
    const [mode, setMode] = useState("new");
    const [stockItems, setStockItems] = useState([]);
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState(emptyEquipmentForm);
    const [notes, setNotes] = useState("");
    const [handover, setHandover] = useState(emptyHandover(currentUserId));
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setMode("new"); setPicked(null); setForm(emptyEquipmentForm); setNotes("");
            setHandover(emptyHandover(currentUserId)); setPhotos([]);
            api.getInventoryItems({ status: "in_stock", limit: 500 }).then((r) => setStockItems(r.items));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const submit = async () => {
        setBusy(true);
        try {
            let targetId = picked;
            if (mode === "new") {
                const created = await api.createInventoryItem(buildEquipmentPayload(form));
                targetId = created.id;
            } else if (!picked) { toast.error("Sélectionnez un matériel en stock"); setBusy(false); return; }
            const res = await api.assignInventoryItem(targetId, {
                user_id: employeeId, notes,
                condition: handover.condition,
                condition_notes: handover.condition_notes,
                handover_date: handover.date_undefined ? null : handover.date,
                handover_date_undefined: !!handover.date_undefined,
                handed_over_by: handover.handed_over_by,
            });
            for (const f of photos) {
                await api.uploadInventoryAttachment(targetId, f, `Photo remise ${handover.date || ""}`.trim(), { category: "etat_des_lieux", historyId: res.history_id });
            }
            toast.success("Matériel attribué");
            onClose();
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Ajouter du matériel</DialogTitle></DialogHeader>
                <ModeToggle mode={mode} setMode={setMode} />
                <div className="max-h-[55vh] overflow-y-auto py-1 pr-1">
                    {mode === "stock"
                        ? <StockPicker items={stockItems} value={picked} onChange={setPicked} />
                        : <EquipmentForm form={form} setForm={setForm} categories={categories} />}
                    <HandoverSection value={handover} onChange={setHandover} photos={photos} setPhotos={setPhotos} people={people} departments={departments} mode="remise" />
                    <div className="mt-3"><label className="form-label text-xs">Note d'attribution (optionnel)</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="add-eq-notes" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={busy} data-testid="confirm-add-equipment-btn">{busy ? "..." : "Attribuer"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReplaceDialog({ item, onClose, categories, onDone, people, departments, currentUserId }) {
    const open = !!item;
    const [mode, setMode] = useState("new");
    const [stockItems, setStockItems] = useState([]);
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState(emptyEquipmentForm);
    const [outcome, setOutcome] = useState("in_stock");
    const [notes, setNotes] = useState("");
    const [handover, setHandover] = useState(emptyHandover(currentUserId));
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setMode("new"); setPicked(null); setOutcome("in_stock"); setNotes("");
            setForm({ ...emptyEquipmentForm, category: item.category });
            setHandover(emptyHandover(currentUserId)); setPhotos([]);
            api.getInventoryItems({ status: "in_stock", limit: 500 }).then((r) => setStockItems(r.items));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const submit = async () => {
        setBusy(true);
        try {
            let newId = picked;
            if (mode === "new") {
                const created = await api.createInventoryItem(buildEquipmentPayload(form));
                newId = created.id;
            } else if (!picked) { toast.error("Sélectionnez le matériel de remplacement"); setBusy(false); return; }
            const res = await api.replaceInventoryItem(item.id, {
                new_item_id: newId, old_outcome: outcome, notes,
                condition: handover.condition,
                condition_notes: handover.condition_notes,
                handover_date: handover.date_undefined ? null : handover.date,
                handover_date_undefined: !!handover.date_undefined,
                handed_over_by: handover.handed_over_by,
            });
            for (const f of photos) {
                await api.uploadInventoryAttachment(newId, f, `Photo remise ${handover.date || ""}`.trim(), { category: "etat_des_lieux", historyId: res.new_history_id });
            }
            toast.success("Matériel remplacé");
            onClose();
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Remplacer "{item?.asset_tag || item?.name}"</DialogTitle></DialogHeader>
                <ModeToggle mode={mode} setMode={setMode} newLabel="Nouveau matériel" stockLabel="Depuis le stock" />
                <div className="max-h-[50vh] overflow-y-auto py-1 pr-1">
                    {mode === "stock"
                        ? <StockPicker items={stockItems} value={picked} onChange={setPicked} />
                        : <EquipmentForm form={form} setForm={setForm} categories={categories} />}
                    <HandoverSection value={handover} onChange={setHandover} photos={photos} setPhotos={setPhotos} people={people} departments={departments} mode="remise" />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div>
                        <label className="form-label text-xs">Que devient l'ancien matériel ?</label>
                        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="replace-outcome">
                            <option value="in_stock">Retour en stock</option>
                            <option value="retired">Retiré du service</option>
                            <option value="broken">Défectueux / endommagé</option>
                        </select>
                    </div>
                    <div><label className="form-label text-xs">Note (optionnel)</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="replace-notes" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={busy} data-testid="confirm-replace-btn">{busy ? "..." : "Remplacer"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ModeToggle({ mode, setMode, newLabel = "Nouveau matériel", stockLabel = "Depuis le stock" }) {
    return (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            <button onClick={() => setMode("new")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${mode === "new" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500"}`} data-testid="mode-new">{newLabel}</button>
            <button onClick={() => setMode("stock")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${mode === "stock" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500"}`} data-testid="mode-stock">{stockLabel}</button>
        </div>
    );
}

function getWarrantyBadge(item) {
    if (!item.warranty_end) return null;
    const diff = (new Date(item.warranty_end) - new Date()) / (1000 * 60 * 60 * 24);
    if (diff < 0) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Garantie expirée</span>;
    if (diff < 30) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Garantie expire bientôt</span>;
    return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Sous garantie</span>;
}

function formatSheetAction(h, names, empId) {
    const actor = names[h.actor_id] || "Système";
    switch (h.action) {
        case "assigned": {
            const remisPar = names[h.details?.handed_over_by] || actor;
            const base = h.details?.replaces_item_name
                ? `Attribué en remplacement de "${h.details.replaces_item_name}"`
                : "Attribué";
            const dateInfo = h.details?.handover_date_undefined ? " — date de remise non définie" : "";
            return `${base} — remis par ${remisPar}${dateInfo}`;
        }
        case "returned_to_stock": {
            const dateInfo = h.details?.return_date_undefined
                ? " — date non définie"
                : (h.details?.return_date ? ` le ${new Date(h.details.return_date).toLocaleDateString("fr-CH")}` : "");
            const rendu = h.details?.returned_by && names[h.details.returned_by] ? ` — rendu par ${names[h.details.returned_by]}` : "";
            return `Restitué au stock${dateInfo}${rendu} (par ${actor})`;
        }
        case "transferred":
            return h.from_user_id === empId
                ? `Transféré à ${names[h.to_user_id] || "?"} par ${actor}`
                : `Reçu de ${names[h.from_user_id] || "?"} (par ${actor})`;
        case "replaced":
            return `Remplacé par "${h.details?.replacement_item_name || "un autre matériel"}" — ancien : ${OUTCOME_LABELS[h.details?.outcome] || h.details?.outcome} (par ${actor})`;
        case "tag_changed":
            return `Asset Tag modifié : ${h.details?.old_tag || "—"} → ${h.details?.new_tag} (par ${actor})`;
        case "signed_doc_uploaded":
            return `${h.details?.doc_type === "restitution_signee" ? "Restitution signée" : "Remise signée"} déposée — signée le ${h.details?.signed_date ? new Date(h.details.signed_date).toLocaleDateString("fr-CH") : "—"} (par ${actor})`;
        case "maintenance_start":
            return `Parti en maintenance (${actor})`;
        case "retired":
            return `Retiré du service par ${actor}`;
        case "lost":
            return `Marqué perdu par ${actor}`;
        case "broken":
            return `Marqué endommagé par ${actor}`;
        default:
            return `${h.action} (${actor})`;
    }
}
